/**
 * Mock User Repository
 *
 * 로컬 Mock 데이터를 사용하여 사용자 데이터를 제공합니다.
 */

import type { User } from '../types/User'
import type { UserRepository } from './UserRepository'
import { mockUsers } from '../mocks/mockUsers'

/**
 * Mock User Repository 구현
 */
export class MockUserRepository implements UserRepository {
  /**
   * 내부 데이터 저장소 (CRUD 작업 가능하도록 mutable)
   */
  private users: User[] = [...mockUsers]

  /**
   * 지연 시뮬레이션 (네트워크 요청 흉내)
   */
  private delay(ms: number = 50): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * ID 생성
   */
  private generateId(): string {
    return `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * 모든 사용자 조회
   */
  async findAll(): Promise<User[]> {
    await this.delay()
    return this.users.map((u) => ({ ...u }))
  }

  /**
   * 활성화된 사용자만 조회
   */
  async findActive(): Promise<User[]> {
    await this.delay()
    return this.users.filter((user) => user.isActive).map((u) => ({ ...u }))
  }

  /**
   * ID로 사용자 조회
   */
  async findById(id: string): Promise<User | null> {
    await this.delay()
    const user = this.users.find((u) => u.id === id)
    return user ? { ...user } : null
  }

  /**
   * 이메일로 사용자 조회
   */
  async findByEmail(email: string): Promise<User | null> {
    await this.delay()
    const user = this.users.find((u) => u.email === email)
    return user ? { ...user } : null
  }

  /**
   * 새 사용자 생성 (초대)
   */
  async create(input: Omit<User, 'id'>): Promise<User> {
    await this.delay()
    const newUser: User = {
      id: this.generateId(),
      ...input,
    }
    this.users.push(newUser)
    return { ...newUser }
  }

  /**
   * 사용자 정보 수정
   */
  async update(id: string, input: Partial<User>): Promise<User> {
    await this.delay()
    const index = this.users.findIndex((u) => u.id === id)
    if (index === -1) {
      throw new Error(`사용자를 찾을 수 없습니다: ${id}`)
    }
    const updated = { ...this.users[index], ...input, id } as User
    this.users[index] = updated
    return { ...updated }
  }

  /**
   * 사용자 삭제 (멤버 제거)
   */
  async delete(id: string): Promise<void> {
    await this.delay()
    const index = this.users.findIndex((u) => u.id === id)
    if (index === -1) {
      throw new Error(`사용자를 찾을 수 없습니다: ${id}`)
    }
    this.users.splice(index, 1)
  }
}
